import React from "react";
import { WebsiteContentProps } from "../../types/wizard.types";
import { students } from "../../utils/mockMembers";
import { useGetClientGymTypeSubType } from "../../../../apis/clientApi.hooks";

const RankTrackingAppDemo: React.FC<WebsiteContentProps> = ({
  brandColor,
  formData,
}) => {
  const businessName = formData?.subdomain
    ? `${formData.subdomain.charAt(0).toUpperCase() + formData.subdomain.slice(1)}`
    : "Your Gym";

  const { data: clientGymTypeSubType } = useGetClientGymTypeSubType();

  const getBeltDisplay = (belt: {
    name: string;
    color: string;
    degree?: string;
  }) => {
    const stripes = belt.degree ? parseInt(belt.degree.charAt(0)) : 0;
    return (
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center">
          <div
            className="tw-w-8 tw-h-3 tw-rounded-sm tw-border tw-border-neutral-300"
            style={{
              backgroundColor:
                belt.color === "#FFFFFF" ? "#F5F5F5" : belt.color,
            }}
          />
          {stripes > 0 && (
            <div className="tw-flex tw-ml-1">
              {Array.from({ length: stripes }).map((_, i) => (
                <div key={i} className="tw-w-1 tw-h-3 tw-bg-black tw-mr-px" />
              ))}
            </div>
          )}
        </div>
        <div className="tw-text-sm">
          <div className="tw-font-medium">{belt.name}</div>
          {belt.degree && (
            <div className="tw-text-xs tw-text-neutral-500">{belt.degree}</div>
          )}
        </div>
      </div>
    );
  };

  const getProgressBar = (progress: number) => (
    <div className="tw-w-full tw-bg-neutral-200 tw-rounded-full tw-h-2">
      <div
        className="tw-h-2 tw-rounded-full tw-transition-all tw-duration-300"
        style={{
          width: `${progress}%`,
          backgroundColor: brandColor,
        }}
      />
    </div>
  );

  return (
    <div className="tw-h-full tw-bg-neutral-50 tw-flex tw-flex-col">
      {/* Header */}
      <div className="tw-bg-white tw-border-b tw-border-neutral-200 tw-p-6">
        <div className="tw-max-w-7xl tw-mx-auto tw-flex tw-justify-between tw-items-center">
          <div>
            <h1 className="tw-text-2xl tw-font-bold tw-text-neutral-900 tw-mb-2">
              {clientGymTypeSubType?.subtype?.name}
            </h1>
            <p className="tw-text-neutral-600">
              Track belt progression and advancement for {businessName} students
            </p>
          </div>
          <button
            className="tw-px-4 tw-py-2 tw-text-white tw-rounded-lg tw-font-medium tw-flex tw-items-center tw-gap-2"
            style={{ backgroundColor: brandColor }}
          >
            <span>+</span>
            Set Promotion Criteria
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="tw-flex-1 tw-overflow-auto tw-p-6">
        <div className="tw-max-w-7xl tw-mx-auto">
          <div className="tw-bg-white tw-rounded-lg tw-border tw-shadow-sm tw-overflow-hidden">
            <table className="tw-w-full">
              <thead className="tw-bg-neutral-50 tw-border-b">
                <tr className="!tw-bg-neutral-200">
                  <th className="!tw-bg-neutral-200 tw-text-left tw-p-4 tw-font-medium tw-text-neutral-600">
                    Name
                  </th>
                  <th className="!tw-bg-neutral-200 tw-text-left tw-p-4 tw-font-medium tw-text-neutral-600">
                    Rank
                  </th>
                  <th className="!tw-bg-neutral-200 tw-text-left tw-p-4 tw-font-medium tw-text-neutral-600">
                    Rank Progress
                  </th>
                  <th className="!tw-bg-neutral-200 tw-text-left tw-p-4 tw-font-medium tw-text-neutral-600">
                    Classes at Rank
                  </th>
                  <th className="!tw-bg-neutral-200 tw-text-left tw-p-4 tw-font-medium tw-text-neutral-600">
                    Next Rank
                  </th>
                  <th className="!tw-bg-neutral-200 tw-w-16"></th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr
                    key={student.id}
                    className="tw-border-b tw-hover:bg-neutral-50"
                  >
                    <td className="tw-p-4">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-overflow-hidden tw-bg-blue-100 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-medium">
                          {student.avatar ? (
                            <img
                              src={student.avatar}
                              alt={student.name}
                              className="tw-w-full tw-h-full tw-object-cover"
                            />
                          ) : (
                            student.initials
                          )}
                        </div>
                        <span className="tw-font-medium">{student.name}</span>
                      </div>
                    </td>
                    <td className="tw-p-4">
                      {getBeltDisplay(student.currentBelt)}
                    </td>
                    <td className="tw-p-4">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {getProgressBar(student.progress)}
                        <span className="tw-text-sm tw-text-neutral-600 tw-whitespace-nowrap">
                          {student.classesAtRank > 0
                            ? `${student.classesAtRank} classes`
                            : `${student.progress}% days`}
                        </span>
                      </div>
                    </td>
                    <td className="tw-p-4 tw-text-center tw-font-medium">
                      {student.classesAtRank}
                    </td>
                    <td className="tw-p-4">
                      {getBeltDisplay(student.nextBelt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankTrackingAppDemo;
